import { FC, useMemo } from 'react';
import { FormField, Select, SpaceBetween, Box, Button, Table, Checkbox } from '@amzn/awsui-components-react';
import { useGetToolMetadataQuery } from 'src/store/apis/toolApi';
import { ToolMetadata, VersionRevision } from 'src/store/interfaces/Tool.types';
import { LLMType, Phase } from 'src/store/interfaces/TestSuite.types';

export interface SelectedOption {
  label: string;
  value: string;
}

export interface ToolRevisionItem {
  toolId: string;
  version: string;
  revision: number;
  status?: string;
}

export const llmTypeOptions: SelectedOption[] = [
  { label: 'ASKRINO VANILLA', value: LLMType.ASKRINO_VANILLA },
  { label: 'ASKRINO AGENTIC', value: LLMType.ASKRINO_AGENTIC },
];

// UAT-only. BLUEY_MCP_AGENT is excluded from llmTypeOptions because its runner's run_test_case raises
// NotImplementedError, so offering it on the test-suite form would fail inside the Step Function.
// BLUEY_MCP_DIRECT is absent from both: it requires toolName/toolParameters per query, which no form offers.
export const uatLlmTypeOptions: SelectedOption[] = [
  ...llmTypeOptions,
  { label: 'BLUEY MCP AGENT', value: LLMType.BLUEY_MCP_AGENT },
];

export const phaseOptions: SelectedOption[] = [
  { label: 'Test', value: Phase.TEST },
  // { label: 'Beta', value: Phase.BETA },
  // { label: 'Live', value: Phase.LIVE },
];

// Hook: derive phase-filtered tools from tool metadata
export function useToolsByPhase() {
  const { data: toolMetadata, isLoading } = useGetToolMetadataQuery();

  const toolsByPhase = useMemo(() => {
    if (!toolMetadata) return null;
    const result: Record<string, ToolRevisionItem[]> = {
      [Phase.TEST]: [],
      [Phase.BETA]: [],
      [Phase.LIVE]: [],
    };
    toolMetadata.forEach((tool: ToolMetadata) => {
      Object.values(Phase).forEach((phase) => {
        const revision = tool[`${phase.toLowerCase()}Revision` as keyof ToolMetadata] as VersionRevision;
        if (revision?.version && revision.revision !== null) {
          result[phase].push({ toolId: tool.toolId, version: revision.version, revision: revision.revision, status: revision.status });
        }
      });
    });
    return result;
  }, [toolMetadata]);

  return { toolsByPhase, isLoading };
}

// Individual select components — render as single FormFields so callers
// can place them freely inside Grid cells or SpaceBetween layouts.

interface LLMTypeSelectProps {
  selectedOption: SelectedOption | null;
  onChange: (option: SelectedOption) => void;
  stretch?: boolean;
  disabled?: boolean;
  options?: SelectedOption[];
}

export const LLMTypeSelect: FC<LLMTypeSelectProps> = ({
  selectedOption,
  onChange,
  stretch,
  disabled,
  options = llmTypeOptions,
}) => (
  <FormField label="LLM Type" stretch={stretch}>
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => onChange(detail.selectedOption as SelectedOption)}
      options={options}
      placeholder="Choose LLM type"
      disabled={disabled}
    />
  </FormField>
);

interface PhaseSelectProps {
  selectedOption: SelectedOption | null;
  onChange: (option: SelectedOption) => void;
  stretch?: boolean;
  disabled?: boolean;
}

export const PhaseSelect: FC<PhaseSelectProps> = ({ selectedOption, onChange, stretch, disabled }) => (
  <FormField label="Phase" stretch={stretch}>
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => onChange(detail.selectedOption as SelectedOption)}
      options={phaseOptions}
      placeholder="Choose phase"
      disabled={disabled}
    />
  </FormField>
);

// Reusable tool selection table
interface ToolSelectionTableProps {
  phase: string | null;
  toolsByPhase: Record<string, ToolRevisionItem[]> | null;
  isLoading: boolean;
  selectedToolIds: string[];
  onSelectionChange: (toolIds: string[]) => void;
  disabled?: boolean;
}

export const ToolSelectionTable: FC<ToolSelectionTableProps> = ({ phase, toolsByPhase, isLoading, selectedToolIds, onSelectionChange, disabled }) => {
  const tools = phase && toolsByPhase ? toolsByPhase[phase] ?? [] : [];
  const allIds = tools.map((t) => t.toolId);

  const toggle = (id: string) => {
    onSelectionChange(selectedToolIds.includes(id) ? selectedToolIds.filter((t) => t !== id) : [...selectedToolIds, id]);
  };

  if (!phase) {
    return (
      <FormField label="Tool Selection" description="Select a phase to view available tools">
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 's' }}>
          Choose a phase first to see available tools.
        </Box>
      </FormField>
    );
  }

  return (
    <FormField
      label={`Tool Selection (${selectedToolIds.length}/${tools.length} selected)`}
      description={`Tools available for ${phase} phase`}
      stretch
    >
      <SpaceBetween size="s">
        {tools.length > 0 && (
          <SpaceBetween direction="horizontal" size="s">
            <Button variant="link" onClick={() => onSelectionChange(allIds)} disabled={disabled || selectedToolIds.length === allIds.length}>
              Select All ({allIds.length})
            </Button>
            <Button variant="link" onClick={() => onSelectionChange([])} disabled={disabled || !selectedToolIds.length}>
              Clear All
            </Button>
          </SpaceBetween>
        )}
        <Table
          variant="embedded"
          loading={isLoading}
          loadingText="Loading tools..."
          items={tools}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No tools available</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No tools found for the selected phase.
              </Box>
            </Box>
          }
          columnDefinitions={[
            { id: 'select', header: '', width: 50, cell: (item: ToolRevisionItem) => <Checkbox checked={selectedToolIds.includes(item.toolId)} onChange={() => toggle(item.toolId)} disabled={disabled} /> },
            { id: 'toolId', header: 'Tool ID', cell: (item: ToolRevisionItem) => item.toolId, sortingField: 'toolId' },
            { id: 'version', header: 'Version', cell: (item: ToolRevisionItem) => item.version, sortingField: 'version' },
            { id: 'revision', header: 'Revision', cell: (item: ToolRevisionItem) => item.revision, sortingField: 'revision' },
            { id: 'status', header: 'Status', cell: (item: ToolRevisionItem) => item.status || 'N/A', sortingField: 'status' },
          ]}
        />
      </SpaceBetween>
    </FormField>
  );
};
